import React from "react";
import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    CircleHelp,
    PencilLine,
    Eye,
    Loader2,
    PauseCircle,
    PlayCircle,
} from "lucide-react";
import {
    formatMachineText,
    getCampaignActionState,
    getCampaignMessageMetrics,
    getCampaignProgressState,
    normalizeCampaignStatus,
} from "../../utils/campaignDisplayUtils";
import { getCampaignNextStepInfo } from "../../utils/campaignScheduleUtils";
import CampaignMetrics from "./CampaignMetrics";
import CampaignStatusBadge from "./CampaignStatusBadge";

const getDisplayValue = (value, fallback = "Unavailable") => value ?? fallback;

const getPositiveCount = (value) => {
    const count = Number(value);

    return Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
};

const getCountText = (count, singular, plural = `${singular}s`) =>
    count === 1 ? `1 ${singular}` : `${count} ${plural}`;

const getNextStepDetailText = (nextStepInfo) => {
    if (
        !nextStepInfo?.hasNextStep ||
        !nextStepInfo.nextStep ||
        nextStepInfo.delayDays === null ||
        nextStepInfo.delayDays === undefined
    ) {
        return "";
    }

    const delayDays = Number(nextStepInfo.delayDays);

    if (!Number.isFinite(delayDays)) return "";

    return `Step ${nextStepInfo.nextStep} Scheduled in ${delayDays} ${
        delayDays === 1 ? "day" : "days"
    }`;
};

const getProgressStatusDisplay = (campaign = {}) => {
    const normalizedStatus = normalizeCampaignStatus(campaign);
    const metrics = campaign?.metrics || {};
    const isPaused = normalizedStatus === "paused";
    const isActive = normalizedStatus === "active";
    const isProcessing =
        normalizedStatus === "processing" || normalizedStatus === "preparing";
    const isCompleted = normalizedStatus === "completed";
    const isFailed = normalizedStatus === "failed";
    const pendingCount = getPositiveCount(metrics.pending);
    const processingCount = getPositiveCount(metrics.processing);
    const failedCount = getPositiveCount(metrics.failed);

    if (isPaused) {
        return {
            Icon: PauseCircle,
            text: "Campaign paused",
            detail: pendingCount
                ? getCountText(
                      pendingCount,
                      "message pending",
                      "messages pending",
                  )
                : "",
            className: "text-amber-700",
        };
    }

    if (isFailed) {
        return {
            Icon: AlertTriangle,
            text: "Campaign needs attention",
            detail: failedCount
                ? getCountText(failedCount, "failed message", "failed messages")
                : "",
            className: "text-red-700",
        };
    }

    if (isCompleted) {
        return {
            Icon: CheckCircle2,
            text: "Campaign completed",
            detail: "",
            className: "text-green-700",
        };
    }

    if (isProcessing) {
        return {
            Icon: Loader2,
            text: "Preparing campaign messages",
            detail: processingCount
                ? getCountText(
                      processingCount,
                      "message processing",
                      "messages processing",
                  )
                : "",
            className: "text-indigo-700",
        };
    }

    if (isActive) {
        return {
            Icon: Activity,
            text: "Campaign active",
            detail: "Outreach in progress",
            className: "text-green-700",
        };
    }

    return {
        Icon: CircleHelp,
        text: "Campaign status unavailable",
        detail: "",
        className: "text-slate-600",
    };
};

const CampaignCard = ({
    campaign = {},
    campaignDetails = null,
    isStatusUpdating = false,
    onEditCampaignContext,
    onUpdateCampaignStatus,
    onViewCampaign,
}) => {
    const helperContext = campaign.ui_helper_context || {};
    const campaignContext = campaign.campaign_context || {};
    const {
        progressPercentage,
        segmentCount,
        hasSegmentedProgress,
        completedSteps,
    } = getCampaignProgressState(campaign);
    const displayProgressPercentage = Math.round(progressPercentage);
    const messageMetrics = getCampaignMessageMetrics(campaign.metrics);
    const totalSteps = getDisplayValue(campaign.total_steps, 0);
    const totalStepCount = getPositiveCount(campaign.total_steps);
    const processedStepsText = totalStepCount
        ? `All ${totalStepCount} steps processed`
        : "All steps processed";
    const completedMessageSummaries = [
        {
            key: "sent",
            text: `${messageMetrics.sent} of ${messageMetrics.totalMessages} messages sent successfully`,
            className: "text-slate-600",
        },
        messageMetrics.skipped > 0
            ? {
                  key: "skipped",
                  text: getCountText(
                      messageMetrics.skipped,
                      "message skipped",
                      "messages skipped",
                  ),
                  className: "text-indigo-600",
              }
            : null,
        messageMetrics.failed > 0
            ? {
                  key: "failed",
                  text: getCountText(
                      messageMetrics.failed,
                      "message failed",
                      "messages failed",
                  ),
                  className: "text-red-600",
              }
            : null,
    ].filter(Boolean);
    const currentActiveStep = getDisplayValue(
        helperContext.current_active_step,
        "Unavailable",
    );
    const pacingText = formatMachineText(helperContext.display_pacing_string);
    const campaignObjective = getDisplayValue(
        campaignContext.campaign_objective,
    );
    const productValueProposition = getDisplayValue(
        campaignContext.product_value_proposition,
    );
    const toneSetting = getDisplayValue(campaignContext.tone_setting);
    const canViewCampaign =
        typeof onViewCampaign === "function" && Boolean(campaign.sequence_id);
    const actionState = getCampaignActionState(campaign);
    const canEditCampaignContext =
        actionState.canEditContext &&
        typeof onEditCampaignContext === "function" &&
        Boolean(campaign.sequence_id);
    const nextStatus = actionState.canPause
        ? "paused"
        : actionState.canSetActive
          ? "active"
          : "";
    const canUpdateCampaignStatus =
        Boolean(nextStatus) &&
        typeof onUpdateCampaignStatus === "function" &&
        Boolean(campaign.sequence_id);
    const statusActionLabel =
        nextStatus === "paused" ? "Pause Campaign" : "Set Active";
    const statusActionLoadingLabel =
        nextStatus === "paused" ? "Pausing..." : "Setting active...";
    const StatusActionIcon = nextStatus === "paused" ? PauseCircle : PlayCircle;
    const progressStatus = getProgressStatusDisplay(campaign);
    const ProgressStatusIcon = progressStatus.Icon;
    const nextStepInfo = getCampaignNextStepInfo(
        campaign,
        campaignDetails || {},
    );
    const nextStepDetail = getNextStepDetailText(nextStepInfo);
    const progressDetails = [progressStatus.detail, nextStepDetail].filter(
        Boolean,
    );
    const normalizedCampaignStatus = normalizeCampaignStatus(campaign);
    const isPausedCampaign = normalizedCampaignStatus === "paused";
    const isCompletedCampaign = normalizedCampaignStatus === "completed";

    const handleViewCampaign = () => {
        if (!canViewCampaign) return;

        onViewCampaign?.(campaign.sequence_id);
    };

    const handleEditCampaignContext = () => {
        if (!canEditCampaignContext) return;

        onEditCampaignContext?.(campaign);
    };

    const handleUpdateCampaignStatus = () => {
        if (!canUpdateCampaignStatus || isStatusUpdating) return;

        onUpdateCampaignStatus?.(campaign, nextStatus);
    };

    return (
        <article className="min-w-0 bg-transparent p-0">
            <div className="flex min-w-0 items-center gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <h3 className="min-w-0 truncate text-base font-semibold text-slate-900">
                        {getDisplayValue(campaign.name, "Untitled campaign")}
                    </h3>
                    {isPausedCampaign && (
                        <PauseCircle
                            size={25}
                            className="shrink-0 text-amber-600 pt-1"
                            aria-label="Paused campaign"
                        />
                    )}
                </div>
                <CampaignStatusBadge
                    status={campaign.status}
                    campaign={campaign}
                    isPaused={helperContext.is_paused}
                />
            </div>

            <div className="mt-3">
                <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-slate-500">
                    <span>Campaign execution:</span>
                    <span>{displayProgressPercentage}%</span>
                </div>
                {hasSegmentedProgress ? (
                    <div className="flex h-2 gap-1">
                        {Array.from({ length: segmentCount }).map(
                            (_, index) => (
                                <span
                                    key={`step-${index + 1}`}
                                    className={`h-full flex-1 rounded-full ${
                                        index < completedSteps
                                            ? "bg-green-600"
                                            : "bg-slate-200"
                                    }`}
                                />
                            ),
                        )}
                    </div>
                ) : (
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                        <div
                            className="h-full rounded-full bg-green-600"
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>
                )}
                {isCompletedCampaign && messageMetrics.totalMessages > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-1 text-xs font-medium">
                        {completedMessageSummaries.map((summary, index) => (
                            <React.Fragment key={summary.key}>
                                {index > 0 && (
                                    <span className="text-slate-400">·</span>
                                )}
                                <p className={summary.className}>
                                    {summary.text}
                                </p>
                            </React.Fragment>
                        ))}
                    </div>
                )}
                <div
                    className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${progressStatus.className}`}
                >
                    <ProgressStatusIcon
                        size={14}
                        className="shrink-0"
                        aria-hidden="true"
                    />
                    <span>
                        {progressStatus.text}
                        {progressDetails.map((detail) => (
                            <React.Fragment key={detail}>
                                <span className="mx-1 text-slate-400">·</span>
                                {detail}
                            </React.Fragment>
                        ))}
                    </span>
                </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-sm xl:grid-cols-3">
                <div className="rounded-lg bg-[#FAFAFF] p-2.5">
                    <p className="text-xs text-slate-500">Total leads</p>
                    <p className="mt-0.5 font-semibold text-slate-900">
                        {getDisplayValue(campaign.total_leads, 0)}
                    </p>
                </div>
                <div className="rounded-lg bg-[#FAFAFF] p-2.5">
                    <p className="text-xs text-slate-500">Total steps</p>
                    <p className="mt-0.5 font-semibold text-slate-900">
                        {totalSteps}
                    </p>
                </div>
                <div className="rounded-lg bg-[#FAFAFF] p-2.5">
                    {isCompletedCampaign ? (
                        <>
                            <p className="text-xs text-slate-500">
                                Campaign steps
                            </p>
                            <p className="mt-0.5 font-semibold text-green-700">
                                {processedStepsText}
                            </p>
                        </>
                    ) : (
                        <div className="flex items-start justify-around gap-2">
                            <div>
                                <p className="text-xs text-slate-500">
                                    Completed steps
                                </p>
                                <p className="mt-0.5 font-semibold text-slate-900">
                                    {completedSteps} of {totalSteps}
                                </p>
                            </div>
                            <span
                                className="h-10 w-px shrink-0 bg-slate-200"
                                aria-hidden="true"
                            />
                            <div className="text-left">
                                <p className="text-xs text-slate-500">
                                    Active step
                                </p>
                                <p className="mt-0.5 font-semibold text-slate-900">
                                    {currentActiveStep} of {totalSteps}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                <div className="rounded-lg bg-[#FAFAFF] p-2.5">
                    <p className="text-xs text-slate-500">Pacing</p>
                    <p
                        className={`mt-0.5 max-h-10 overflow-hidden font-semibold ${
                            isCompletedCampaign
                                ? "text-green-700"
                                : "text-slate-900"
                        }`}
                        title={pacingText}
                    >
                        {pacingText}
                    </p>
                </div>
                <div className="rounded-lg bg-[#FAFAFF] p-2.5">
                    <p className="text-xs text-slate-500">
                        Delivery success rate
                    </p>
                    <p className="mt-0.5 font-semibold text-slate-900">
                        {getDisplayValue(helperContext.delivery_success_rate)}
                    </p>
                </div>
                <div className="rounded-lg bg-[#FAFAFF] p-2.5">
                    <p className="text-xs text-slate-500">Bounce count</p>
                    <p className="mt-0.5 font-semibold text-slate-900">
                        {getDisplayValue(helperContext.bounce_count, 0)}
                    </p>
                </div>
            </div>

            <div className="mt-3">
                <CampaignMetrics
                    metrics={campaign.metrics}
                    nextStepInfo={nextStepInfo}
                />
            </div>

            <div className="mt-3 grid gap-2 text-sm xl:grid-cols-3">
                <div className="rounded-lg bg-[#FAFAFF] p-2.5">
                    <p className="text-xs font-semibold uppercase text-slate-500">
                        Campaign objective
                    </p>
                    <p
                        className="mt-1 max-h-20 overflow-hidden break-words text-sm leading-5 text-slate-800"
                        title={campaignObjective}
                    >
                        {campaignObjective}
                    </p>
                </div>
                <div className="rounded-lg bg-[#FAFAFF] p-2.5">
                    <p className="text-xs font-semibold uppercase text-slate-500">
                        Product value proposition
                    </p>
                    <p
                        className="mt-1 max-h-20 overflow-hidden break-words text-sm leading-5 text-slate-800"
                        title={productValueProposition}
                    >
                        {productValueProposition}
                    </p>
                </div>
                <div className="rounded-lg bg-[#FAFAFF] p-2.5">
                    <p className="text-xs font-semibold uppercase text-slate-500">
                        Tone setting
                    </p>
                    <p
                        className="mt-1 max-h-20 overflow-hidden break-words text-sm leading-5 text-slate-800"
                        title={toneSetting}
                    >
                        {toneSetting}
                    </p>
                </div>
            </div>

            <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                {canUpdateCampaignStatus && (
                    <button
                        type="button"
                        onClick={handleUpdateCampaignStatus}
                        disabled={isStatusUpdating}
                        className={`inline-flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto ${
                            nextStatus === "paused"
                                ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                : "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                        }`}
                    >
                        {isStatusUpdating ? (
                            <Loader2
                                size={16}
                                className="animate-spin"
                                aria-hidden="true"
                            />
                        ) : (
                            <StatusActionIcon size={16} aria-hidden="true" />
                        )}
                        {isStatusUpdating
                            ? statusActionLoadingLabel
                            : statusActionLabel}
                    </button>
                )}
                {canEditCampaignContext && (
                    <button
                        type="button"
                        onClick={handleEditCampaignContext}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-100 sm:w-auto"
                    >
                        <PencilLine size={16} aria-hidden="true" />
                        Edit Campaign Context
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleViewCampaign}
                    disabled={!canViewCampaign}
                    title={
                        canViewCampaign
                            ? "View campaign"
                            : "Campaign details will be available soon."
                    }
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300 sm:w-auto"
                >
                    <Eye size={16} aria-hidden="true" />
                    View Campaign
                </button>
            </div>
        </article>
    );
};

export default CampaignCard;
